'use client';

import { useEffect, useMemo, useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SizeSelector } from './SizeSelector';
import type { MerchProduct } from '../interfaces/merch';

interface CheckoutModalProps {
  product: MerchProduct | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    buyer_email: string;
    buyer_name?: string;
    quantity: number;
    selected_size?: string;
  }) => Promise<void>;
  isLoading?: boolean;
  selectedSize?: string;
  onSizeChange?: (size: string) => void;
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function CheckoutModal({
  product,
  open,
  onClose,
  onConfirm,
  isLoading,
  selectedSize,
  onSizeChange,
}: CheckoutModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [emailError, setEmailError] = useState('');
  const [sizeError, setSizeError] = useState('');

  const variants = product?.variants ?? [];
  const hasVariants = variants.length > 0;

  const selectedVariant = useMemo(
    () => variants.find((v) => v.size === selectedSize),
    [variants, selectedSize],
  );

  const maxQty = useMemo(() => {
    if (!product) return 1;
    if (hasVariants) return Math.max(1, selectedVariant?.stock_quantity ?? 0);
    return Math.max(1, product.stock_quantity);
  }, [product, hasVariants, selectedVariant]);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setName('');
      setQuantity(1);
      setEmailError('');
      setSizeError('');
    }
  }, [open]);

  useEffect(() => {
    if (product) setQuantity(1);
  }, [product?.id, selectedSize]);

  if (!product) return null;

  const total = Number(product.price) * quantity;
  const emailValid = isValidEmail(email);
  const sizeValid = !hasVariants || Boolean(selectedSize);

  const handleSubmit = async () => {
    if (!emailValid) {
      setEmailError('Ingresa un email válido');
      return;
    }
    if (!sizeValid) {
      setSizeError('Selecciona una talla');
      return;
    }
    setEmailError('');
    setSizeError('');
    await onConfirm({
      buyer_email: email.trim(),
      buyer_name: name.trim() || undefined,
      quantity,
      selected_size: selectedSize,
    });
  };

  const handleQuantityChange = (value: number) => {
    setQuantity(Math.min(maxQty, Math.max(1, value)));
  };

  return (
    <CustomModal
      isOpen={open}
      onClose={onClose}
      title={`Comprar ${product.name}`}
      description="Serás redirigido a Mercado Pago para completar el pago de forma segura."
    >
      <div className="space-y-4">
        <div
          className="rounded-xl border p-4"
          style={{
            borderColor: 'color-mix(in srgb, var(--merch-accent) 25%, transparent)',
            background: 'color-mix(in srgb, var(--merch-accent) 8%, transparent)',
          }}
        >
          <p className="font-medium">{product.name}</p>
          {selectedSize && (
            <p className="text-sm text-muted-foreground mt-1">Talla: {selectedSize}</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            {quantity} ×{' '}
            {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(
              Number(product.price),
            )}
          </p>
          <p className="text-lg font-semibold text-[var(--merch-accent)] mt-2">
            Total:{' '}
            {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(total)}
          </p>
        </div>

        {hasVariants && onSizeChange && (
          <div className="space-y-2">
            <SizeSelector
              variants={variants}
              selectedSize={selectedSize}
              onSelect={(size) => {
                onSizeChange(size);
                if (sizeError) setSizeError('');
              }}
            />
            {sizeError && <p className="text-xs text-destructive">{sizeError}</p>}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="buyer-email">Email *</Label>
          <Input
            id="buyer-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            placeholder="tu@email.com"
            aria-invalid={!!emailError}
          />
          {emailError && <p className="text-xs text-destructive">{emailError}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="buyer-name">Nombre (opcional)</Label>
          <Input
            id="buyer-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="quantity">Cantidad</Label>
          <Input
            id="quantity"
            type="number"
            min={1}
            max={maxQty}
            value={quantity}
            onChange={(e) => handleQuantityChange(Number(e.target.value) || 1)}
            disabled={hasVariants && !selectedSize}
          />
          <p className="text-xs text-muted-foreground">
            {hasVariants && !selectedSize
              ? 'Selecciona una talla para ver el máximo disponible'
              : `Máximo ${maxQty} unidades`}
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            className="flex-1 bg-[var(--merch-accent)] text-white hover:bg-[color-mix(in_srgb,var(--merch-accent)_85%,black)]"
            disabled={isLoading || !emailValid || !sizeValid || maxQty <= 0}
            onClick={() => void handleSubmit()}
          >
            {isLoading ? 'Procesando...' : 'Ir a Mercado Pago'}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
}
